import { useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { useStore } from "zustand";
import { useShallow } from "zustand/react/shallow";

import { useBasketProducts } from "~/providers/app";

import { getBasket } from "./getters";
import { basketStore } from "./store";
import type { BasketStore } from "./store.types";

function useBasket(): BasketStore {
  return useStore(basketStore, useShallow(getBasket));
}

function useBasketWithCategories() {
  const { products: basket } = useBasket();
  const { productsInBasket, defaultTaxData } = useBasketProducts();

  return useMemo(() => {
    return basket.flatMap((product) => {
      const productId = product.productId;

      if (product.type === "custom") {
        const customVatRate = product.customVatRate;
        const vatRate = customVatRate ? customVatRate : defaultTaxData.taxRate;

        const exclVatUnitPrice =
          product.vatCalculation === "excl"
            ? product.rawUnitPrice
            : product.unitPrice / (1 + vatRate);

        return {
          amount: product.count,
          category: product.category,
          unitPrice: exclVatUnitPrice,
          name: product.name,
          id: productId,
          type: product.type,
          productLineId: product.id,
          taxRateName: customVatRate ? null : defaultTaxData.taxRateName,
          taxRateId: customVatRate ? null : defaultTaxData.taxRateId,
          vatRate
        };
      }

      const productInBasket = productsInBasket.find(
        (p) => p.basketId === product.id
      );
      if (!productInBasket) return [];

      return {
        amount: product.count,
        unitPrice: productInBasket.exclVatUnitPrice,
        vatRate: productInBasket.vatRate,
        category: productInBasket.category,
        productLineId: productInBasket.productLineId,
        id: productId,
        type: product.type,
        taxRateId: productInBasket.taxRateId,
        taxRateName: productInBasket.taxRateName,
        stampCardRuleId: productInBasket.stampCardRuleId,
        ...((product.type === "variant" ||
          product.type === "variant-weight") && {
          options: product.options.map((option) => ({
            id: "id" in option ? option.id : uuidv4(),
            choices: option.choices
              .filter((choice) => choice.amount > 0)
              .map((choice) => ({
                id: choice.id,
                amount: choice.amount,
                name: choice.name ?? "",
                ...("unitPrice" in choice && {
                  unitPrice: choice.unitPrice ?? 0
                }),
                ...("weight" in choice && { weight: choice.weight })
              }))
          }))
        }),
        ...(productInBasket.discountUnitPrice && {
          unitDiscountedPrice: productInBasket.discountUnitPrice
        }),
        // hybrid billing
        ...(productInBasket.discount && {
          discount: productInBasket.discount
        })
      };
    });
  }, [basket, productsInBasket, defaultTaxData]);
}

function useOrderDiscount(): BasketStore["orderDiscount"] {
  return useStore(basketStore, (state) => state.orderDiscount);
}

function useDiningOption(): BasketStore["diningOption"] {
  return useStore(basketStore, (state) => state.diningOption);
}

function useShopperName(): BasketStore["shopperName"] {
  return useStore(basketStore, (state) => state.shopperName);
}

export {
  useBasket,
  useBasketWithCategories,
  useDiningOption,
  useOrderDiscount,
  useShopperName
};
